/**
 * Display formatting.
 *
 * All money arrives from the API as an exact decimal string and is formatted
 * here for display only — it is never parsed into a number and back.
 */

import { formatMoney as sharedFormatMoney } from '@cenox/shared';

export function money(amount: string | null | undefined, currency: string): string {
  if (amount === null || amount === undefined) return '—';
  return sharedFormatMoney(amount, currency);
}

/**
 * Compact money for stat tiles, where "₦1.2M" reads faster than the full
 * figure. The exact value stays available in a title attribute.
 */
export function moneyCompact(amount: string | null | undefined, currency: string): string {
  if (amount === null || amount === undefined) return '—';

  const value = Number(amount);
  if (!Number.isFinite(value)) return money(amount, currency);
  if (Math.abs(value) < 100_000) return sharedFormatMoney(amount, currency);

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return sharedFormatMoney(amount, currency);
  }
}

/**
 * What goes before a hand-built amount: "₦" for the naira, or the code and a
 * space for a currency with no symbol of its own. Prefer {@link money}.
 */
export function currencyPrefix(code: string): string {
  try {
    const symbol = new Intl.NumberFormat('en-US', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value;
    return symbol && symbol !== code ? symbol : `${code} `;
  } catch {
    return `${code} `;
  }
}

export function count(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
}

/** A date, in the business's timezone. */
export function date(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: timezone,
  });
}

export function dateTime(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/** Relative time for the audit trail: "3 minutes ago". */
export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Today, as YYYY-MM-DD in the given timezone — for date input defaults. */
export function todayIn(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** ISO instant for a `datetime-local` input value, and back. */
export function toDateTimeLocal(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

/**
 * The instant a `datetime-local` value means in `timezone` — the inverse of
 * `toDateTimeLocal`. A slip that says 14:30 means 14:30 at the pharmacy,
 * whatever zone the manager's laptop is set to.
 */
export function fromDateTimeLocal(local: string, timezone: string): string {
  const [date = '', time = '00:00'] = local.split('T');
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const wall = Date.UTC(y!, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  // Guess, see what wall time the guess shows in the zone, correct by the gap; twice covers DST edges.
  let guess = wall;
  for (let i = 0; i < 2; i++) {
    const shown = toDateTimeLocal(new Date(guess).toISOString(), timezone);
    const [sd = '', st = '00:00'] = shown.split('T');
    const [sy, smo, sdd] = sd.split('-').map(Number);
    const [sh, smi] = st.split(':').map(Number);
    guess += wall - Date.UTC(sy!, (smo ?? 1) - 1, sdd ?? 1, sh ?? 0, smi ?? 0);
  }
  return new Date(guess).toISOString();
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
